import { HELP_ANCHORS, helpAnchor } from "@keydials/ui";
import { Icon } from "../components/Icon";
import type { SegmentNoun } from "../vocabulary";
import {
  type ChecklistFrame,
  type ChecklistRow,
  type OverviewChecklistModel,
  checklistHeadline,
  rowStateText,
} from "./overviewChecklistModel";

const GREEN = "var(--rag-green, #16a34a)";
const AMBER = "var(--st-amber, #b45309)";
const NOTE = { margin: 0, fontSize: 14, lineHeight: 1.6, color: "var(--text-muted)" } as const;

/**
 * The action point's Overview list (KEY-569): the fields it asks for and which still need an answer, each
 * one this viewer can fill in a click away from its place on the Info tab.
 *
 * Presentational only. {@link buildOverviewChecklist} decides what is listed and how each answer reads, and
 * is where those rules are tested.
 */
export function OverviewChecklist({ model, frame, noun, statusName, pendingStatusName, simple, onJump }: {
  model: OverviewChecklistModel;
  frame: ChecklistFrame;
  noun: SegmentNoun;
  /** The action point's own status, named when that status means nothing here is owed. */
  statusName?: string;
  /** The status a parked pick is waiting to apply, named on the fields it's waiting on. */
  pendingStatusName?: string;
  /** Whether the action point carries its own status. Only then is "nothing to fill in" worth saying: an
   * item-scored one keeps its work on the Items tab, and the line above this already says how that's going. */
  simple: boolean;
  /** Opens a field on the Info tab. Absent when there is no Info tab to open. */
  onJump?: (row: ChecklistRow) => void;
}) {
  if (frame === "excluded") {
    return (
      <p style={NOTE}>
        This {noun.one} is marked {statusName ?? "not required"}, so nothing here counts towards the dial.
      </p>
    );
  }
  if (model.total === 0) {
    return simple ? <p style={NOTE}>There&rsquo;s nothing to fill in here.</p> : null;
  }

  const headline = checklistHeadline(model, frame);
  // Nothing left to chase: the rows stay, as a record of what was given, but stop asking for attention.
  const quiet = frame === "record" || model.filled === model.total;

  return (
    <div {...helpAnchor(HELP_ANCHORS.apOverviewChecklist)} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {headline && <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: "var(--text)" }}>{headline}</p>}
      {model.groups.map((group, index) => (
        <div key={`${index}:${group.title ?? ""}`} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {group.title && (
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-muted)", marginBottom: 2 }}>{group.title}</div>
          )}
          {/* A role'd list rather than a <ul>: Tailwind's preflight strips list markers in the user app. */}
          <div
            role="list"
            aria-label={group.title ?? `What this ${noun.one} asks for`}
            style={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            {group.rows.map((row) => (
              <div role="listitem" key={row.id}>
                <ChecklistRowView
                  row={row}
                  frame={frame}
                  quiet={quiet}
                  pendingStatusName={pendingStatusName}
                  onJump={row.editable ? onJump : undefined}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      {frame === "todo" && !model.anyEditable && model.filled < model.total && (
        <p style={{ ...NOTE, fontSize: 13 }}>
          You can see these on the Key Info tab — someone who looks after this dial fills them in.
        </p>
      )}
    </div>
  );
}

function ChecklistRowView({ row, frame, quiet, pendingStatusName, onJump }: {
  row: ChecklistRow;
  frame: ChecklistFrame;
  quiet: boolean;
  pendingStatusName?: string;
  onJump?: (row: ChecklistRow) => void;
}) {
  const filled = row.answer === "filled";
  const gone = row.answer === "unavailable";
  // A finished action point isn't nagged, so what it would have needed stops being said.
  const asking = frame === "todo";
  const notes = [
    rowStateText(row, frame),
    asking && row.neededFirst ? "Needed before items can start" : null,
    asking && row.demanded && pendingStatusName ? `Needed for ${pendingStatusName}` : null,
  ].filter((note): note is string => !!note);

  const body = (
    <>
      <span
        aria-hidden="true"
        style={{
          flex: "none",
          marginTop: 2,
          width: 18,
          height: 18,
          borderRadius: 999,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: filled ? GREEN : "transparent",
          border: filled ? "none" : `1.5px solid ${gone ? AMBER : "var(--text-faint)"}`,
          color: gone ? AMBER : "#fff",
          fontSize: 11,
          fontWeight: 800,
        }}
      >
        {filled ? <Icon name="check" size={12} /> : gone ? "!" : null}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: quiet ? "var(--text-muted)" : "var(--text)" }}>
          {row.decl.label}
        </span>
        <span style={{ display: "block", marginTop: 1, fontSize: 12.5, color: gone ? AMBER : "var(--text-faint)" }}>
          {notes.join(" · ")}
        </span>
      </span>
      {onJump && (
        <Icon name="chevDown" size={16} style={{ flex: "none", marginTop: 3, color: "var(--text-faint)", transform: "rotate(-90deg)" }} />
      )}
    </>
  );

  const layout = { display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 10px", borderRadius: "var(--radius)" } as const;
  if (!onJump) {
    return <div style={layout}>{body}</div>;
  }
  return (
    <button
      type="button"
      className="kd-focusable"
      title="Open this on the Key Info tab"
      onClick={() => onJump(row)}
      style={{ ...layout, width: "100%", font: "inherit", color: "inherit", textAlign: "left", border: "none", background: "none", cursor: "pointer" }}
    >
      {body}
    </button>
  );
}
